import { useEffect, useMemo, useRef, useState } from "react";
import { gql } from "@apollo/client";
import { FaustTemplate } from "@faustwp/core";
import {
  InducteeHero,
  InducteeImpactHero,
  SEO,
  InducteeInductionHero,
  InducteeInfluence,
  CarouselShop,
  Title,
  Feature,
  GridInductees,
  VideoCarousel,
  InducteeMembers,
  Intro,
  StickyNav,
  SectionEnd,
  ConnectedInductees,
} from "@components";
import { InducteeQuery } from "@graphqlTypes";
import { ACFMediaFragment, InducteeInfoFragment } from "@/fragments";
import { usePageHighlight } from "@/providers/PageHighlightContext";
import { TheirInductionFragment, TheirImpactFragment, TheirInfluenceFragment, AdditionalContentFragment } from "@/fragments/Inductee";
import { InducteeImpactMap } from "@/components/ComponentMaps";
import { useDebug } from "@/utils/useDebugLog";
import { cleanHeadline, getBasicQueryVars, getPageTitle, getUrlLocale } from '@/core/utils';
import { PageDataFragment } from "@/fragments/PageData";
import { HIGHLIGHT_COLORS } from "@/core/constants";
import { usePageLoading } from "@/providers/LoadingContext";
import { ScrollProvider } from "@/providers/ScrollContext";
import { useTrackRouteChange } from "@/providers/TrackingContext";
import { usePageWrapperData } from "@/providers/PageWrapperContext";

const Inductee: FaustTemplate<InducteeQuery> = (props: FaustPageProps<InducteeQuery>) => {
  const { log } = useDebug({ name: 'SingleInductee', bgColor: 'deeppink', textColor: 'white', enabled: false });

  // -------------------- Highlight Color
  const { data, loading, pageWrapperData } = props;

  const { setHighlightColor } = usePageHighlight();
  const { previewLoading, setPreviewLoading } = usePageLoading();
  const { siteTitle } = usePageWrapperData();

  // Destructure the data
  const { rhInductee } = data || {};
  const {
    title,
    pageData: { ogTitle, ogDescription, featuredImage, pageCategory } = {},
    inducteeMembers,
    inducteeData: {
      category,
      includeInductionSection,
      includeInfluenceSection,
      includeImpactSection,
      inductedBy,
      intro,
      inducteeClass,
      inductionHero,
      includeVideoCarousel,
      inductionVideoCarousel,
      includeConnectedInductees,
      connectedInductees,
      impactHero,
      impactFlexibleContent,
      inducteeHero,
      inducteeInfluence,
      membersSection,
      overrideDefaultFeature,
      feature,
      includeShopCarousel,
      inducteeShopCarousel,
      gridInductees,
    } = {}
  } = rhInductee || {};
  const [pageCat] = pageCategory || [];
  const inducteeInfo = {
    title,
    inductedBy,
    category: category?.[0] || "",
    class: inducteeClass?.[0]?.slug || "",
  };

  const seoTitle = useMemo(() => getPageTitle(siteTitle, ogTitle || title), [pageWrapperData, ogTitle, title]);
  const featureHeadline = useMemo(() => {
    if (overrideDefaultFeature && feature?.headline) {
      return feature.headline;
    }
    return pageWrapperData.siteSettings?.customSettings?.defaultIdpFeature?.headline || '';
  }, [feature, overrideDefaultFeature]);

  const featureItems = useMemo(() => {
    if (overrideDefaultFeature && feature?.items) {
      return feature.items;
    }
    return pageWrapperData.siteSettings?.customSettings?.defaultIdpFeature?.items || [];
  }, [feature, overrideDefaultFeature]);

  // -------------------- Sticky Nav

  // Section Refs
  const sectionRef_Induction =  useRef<HTMLElement>(null);
  const sectionRef_Impact =  useRef<HTMLElement>(null);
  const sectionRef_Influence =  useRef<HTMLElement>(null);

  // Build a centralized array of sticky nav sections so we can derive labels, refs, and markup in one place
  const stickySections = [
    {
      id: 'idp-section-induction',
      include: !!includeInductionSection,
      sectionRef: sectionRef_Induction,
      label: cleanHeadline(inductionHero?.headline, 'Induction'),
    },
    {
      id: 'idp-section-impact',
      include: true,
      sectionRef: sectionRef_Impact,
      label: cleanHeadline(impactHero?.headline, 'Impact'),
    },
    {
      id: 'idp-section-influence',
      include: !!includeInfluenceSection,
      sectionRef: sectionRef_Influence,
      label: cleanHeadline(inducteeInfluence?.headline, 'Influence'),
    },
  ];

  // Determine if we have all 3 sections to show the StickyNav (based on include flags)
  const hasStickyNav: boolean = stickySections.filter(s => s.include).length === 3;
  // const hasStickyNav: boolean = false; // Debug: Disable sticky nav

  // Track which section the StickyNav reports as active (per-section map) - initialize from stickySections
  const [stickyActiveSections, setStickyActiveSections] = useState<Record<string, boolean>>(
    stickySections.reduce((acc, s) => { acc[s.id] = false; return acc; }, {} as Record<string, boolean>)
  );

  const showMembersSection = useMemo(() => {
    return inducteeMembers?.every(m => m.name && m.signature);
  }, [inducteeMembers]);

  useEffect(() => {
    setHighlightColor(HIGHLIGHT_COLORS.YELLOW);
  }, [pageCat]);

  useTrackRouteChange(pageCat?.slug);

  // -------------------- Loading State
  useEffect(() => {
    if (!loading && previewLoading) setPreviewLoading(false);
  }, [loading, previewLoading]);

  if (loading) return;

  // -------------------- Render

  return (
    <>
      <SEO title={seoTitle} description={ogDescription} image={featuredImage} />

        {/* Hero section */}
        <InducteeHero
          data={inducteeHero}
          inducteeInfo={inducteeInfo}
        />

        {/* Intro section */}
        <Intro { ...intro } inducteeDetail={true} />

        {/* Sticky Nav */}
        { hasStickyNav && (
          log('Rendering StickyNav'),
          <ScrollProvider>
            <StickyNav
              items={stickySections.filter(s => s.include).map(s => ({ label: s.label, sectionId: s.id }))}
              showImmediately={false}
              onActiveSectionChange={(sectionId) => {
                setStickyActiveSections(prev => {
                  const next: Record<string, boolean> = { ...prev };
                  if (sectionId && !next[sectionId]) {
                    next[sectionId] = true;
                  }
                  return next;
                });
              }}
              centerActiveAtMid={true}
              fadeUntilActive={true}
            />
          </ScrollProvider>
        )}

        {/* -------------------- Induction -------------------- */}
        { includeInductionSection && (
          <>
            <section id="idp-section-induction" ref={sectionRef_Induction}>
              <InducteeInductionHero {...inductionHero} />
              { includeVideoCarousel && <VideoCarousel {...inductionVideoCarousel} /> }
              { includeConnectedInductees && connectedInductees && <ConnectedInductees {...connectedInductees} /> }
            </section>
            { hasStickyNav && <SectionEnd sectionId="idp-section-induction" /> }
          </>
        )}
        {/* -------------------- /Induction ------------------- */}

        {/* -------------------- Impact ----------------------- */}
        { includeImpactSection && (
          <section id="idp-section-impact" ref={sectionRef_Impact}>
            <InducteeImpactHero {...impactHero} />
            <InducteeImpactMap content={impactFlexibleContent} />
          </section>
        )}

        { hasStickyNav && <SectionEnd sectionId="idp-section-impact" /> }
        {/* -------------------- /Impact ---------------------- */}

        {/* -------------------- Influence -------------------- */}
        { includeInfluenceSection && (
          <>
            <section id="idp-section-influence" ref={sectionRef_Influence}>
              <InducteeInfluence inducteeInfluence={inducteeInfluence} inducteeName={title} />
            </section>
            { hasStickyNav && <SectionEnd sectionId="idp-section-influence" /> }
          </>
        )}
        {/* -------------------- /Influence-------------------- */}

        { showMembersSection && <InducteeMembers content={membersSection} members={inducteeMembers} /> }

        {/* Additional Content */}
        { includeShopCarousel && <CarouselShop {...inducteeShopCarousel} /> }
        { featureHeadline && <Title headline={featureHeadline} /> }
        { featureItems?.length ? <Feature items={featureItems} float={false} /> : null }
        { gridInductees?.headline && (
            <GridInductees
              headline={gridInductees?.headline}
              cta={gridInductees?.cta}
              inductees={gridInductees?.featuredInductees.nodes}
            />
          )
        }

      </>
  );

};

Inductee.query = gql`
  ${ACFMediaFragment}
  ${InducteeHero.fragments.entry}
  ${InducteeInfoFragment}
  ${TheirImpactFragment}
  ${TheirInductionFragment}
  ${TheirInfluenceFragment}
  ${AdditionalContentFragment}
  ${PageDataFragment}
  query Inductee($databaseId: ID!, $asPreview: Boolean = false, $language: String = "en") {
    rhInductee(id: $databaseId, asPreview: $asPreview, language: $language) {
      title
      ...PageData
      ...InducteeHero
      ...InducteeInfo
      ...TheirInduction
      ...TheirImpact
      ...TheirInfluence
      ...AdditionalContent
    }
  }
`;

Inductee.variables = getBasicQueryVars;

export default Inductee;
